import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../api/client";
import {
  applyHome,
  getHome,
  type HomeState,
  type HomeWidget,
  resetHome,
  revertHome,
  validateHome,
} from "../../api/hub/home";
import { HomeGrid } from "./HomeGrid";
import { ConfigPanel } from "./ConfigPanel";
import { HomeLibrary } from "./HomeLibrary";
import { HomeHistoryPanel } from "./HomeHistoryPanel";
import { DataState, HomePanel, WorkDeskHeader } from "./Frame";
import { dateLabel, homeKeys, message, useWorkspaceMe } from "./data";
import { addWidget, localLayoutError, sortedWidgets } from "./layout";
import { useHomeEditing } from "./useHomeEditing";
import { widgetTitle } from "./Widget";
type AiChange = {
  version: number;
  targetVersion: number;
  summary: string[];
  keyName: string;
};
export function PersonalHomePage() {
  const me = useWorkspaceMe(),
    client = useQueryClient(),
    query = useQuery({
      queryKey: homeKeys.state(),
      queryFn: getHome,
      refetchInterval: 1500,
      refetchIntervalInBackground: false,
    });
  const editor = useHomeEditing(), observed = useRef<HomeState | null>(null);
  const [panel, setPanel] = useState<
      "library" | "history" | "ai" | "compare" | null
    >(null),
    [configuring, setConfiguring] = useState<HomeWidget | null>(null);
  const [aiChange, setAiChange] = useState<AiChange | null>(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState(""),
    [forcedConflict, setForcedConflict] = useState(false);
  const state = query.data,
    descriptors = state?.widgets ?? [],
    draft = editor.draft;
  const conflict = forcedConflict ||
    !!(draft && state &&
      (draft.baseVersion !== state.version ||
        draft.baseLayout !== JSON.stringify(state.layout)));
  useEffect(() => {
    const next = query.data;
    if (!next) return;
    const previous = observed.current;
    if (previous && next.version > previous.version) {
      if (next.author?.kind === "ai" && next.author.keyId) {
        setAiChange({
          version: next.version,
          targetVersion: previous.version,
          summary: next.changeSummary,
          keyName: next.author.keyName || next.author.keyId,
        });
      } else setAiChange(null);
    }
    observed.current = next;
  }, [query.data]);
  const accept = async (next: HomeState, note: string) => {
    editor.setDraft(null);
    setForcedConflict(false);
    setPanel(null);
    setConfiguring(null);
    setAiChange(null);
    setError("");
    setNotice(note);
    client.setQueryData(homeKeys.state(), next);
    await client.invalidateQueries({ queryKey: homeKeys.history() });
  };
  const perform = async (operation: () => Promise<HomeState>, note: string) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await accept(await operation(), note);
    } catch (e) {
      setError(message(e));
      if (e instanceof ApiError && e.status === 409) {
        setForcedConflict(true);
        await query.refetch();
      }
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    if (!state || !draft || conflict) return;
    const invalid = localLayoutError(draft.layout, descriptors);
    if (invalid) {
      setError(invalid);
      return;
    }
    await perform(async () => {
      const checked = await validateHome(draft.layout, draft.baseVersion);
      return applyHome(checked.draftId, checked.expectedVersion);
    }, "主页已保存为新版本，标准仪表盘保持原样。");
  };
  const begin = () => {
    if (!state) return;
    if (editor.mobile) {
      setNotice("手机可以查看、撤销和恢复版本。请在桌面编辑布局。");
      return;
    }
    editor.begin(state.layout, state.version);
    setError("");
    setNotice("");
    setForcedConflict(false);
  };
  const cancel = () => {
    editor.setDraft(null);
    setForcedConflict(false);
    setConfiguring(null);
    setPanel(null);
    setError("");
    setNotice("已取消草稿，当前布局与历史记录保留。");
  };
  const currentLayout = draft?.layout ?? state?.layout;
  return (
    <main className="home-page">
      <WorkDeskHeader
        title="我的主页"
        mode={draft ? "编辑中" : undefined}
        description={draft
          ? "调整部件后保存。"
          : "今天的工作，按你的习惯排列。"}
        actions={!draft
          ? (
            <>
              <button
                className="home-primary"
                disabled={!state || busy}
                onClick={begin}
              >
                编辑首页
              </button>
            </>
          )
          : undefined}
      />
      {editor.navigationBlocked && editor.draft && (
        <p className="home-notice" role="status">
          草稿尚未保存，请先保存或取消，再离开工作桌。
        </p>
      )}
      {notice && <p className="home-notice" role="status">{notice}</p>}
      {error && <p className="home-error" role="alert">{error}</p>}
      {aiChange && state && (
        <section className="home-ai-change" aria-label="AI 主页改动">
          <div>
            <strong>你的 AI 刚改了首页</strong>
            <p>
              {aiChange.keyName} · v{aiChange.version} ·{" "}
              {aiChange.summary.join("；") || "布局已更新"}
            </p>
          </div>
          <button
            disabled={busy || !!draft || state.version !== aiChange.version}
            onClick={() =>
              void perform(
                () =>
                  aiChange.targetVersion === 0
                    ? resetHome(state.version)
                    : revertHome(aiChange.targetVersion, state.version),
                "已撤销这次 AI 改动，撤销也保留为新版本。",
              )}
          >
            一键撤销 AI 改动
          </button>
        </section>
      )}
      <DataState query={query}>
        <>
          {state && (
            <>
              {!draft && (
                <details className="home-preferences">
                  <summary>主页设置</summary>
                  <div className="home-scope-strip">
                    <span>
                      布局来源 · {state.source === "personal"
                        ? "个人配置"
                        : state.source === "role"
                        ? state.defaultRole + " 角色默认"
                        : "系统默认"} · v{state.version}
                      {state.createdAt &&
                        " · " +
                          dateLabel(state.createdAt, me.data?.data.timezone)}
                    </span>
                    <div className="home-actions">
                      <button
                        disabled={busy}
                        onClick={() => setPanel("history")}
                      >
                        版本历史
                      </button>
                      <button disabled={busy} onClick={() => setPanel("ai")}>
                        让我的 AI 改
                      </button>
                      <Link to="/dashboard">标准仪表盘 ↗</Link>
                      <button
                        disabled={busy}
                        onClick={() =>
                          void perform(
                            () => resetHome(state.version),
                            "已恢复默认，之后持续跟随当前角色默认布局。",
                          )}
                      >
                        恢复角色默认
                      </button>
                    </div>
                  </div>
                </details>
              )}
              {draft && (
                <>
                  <div className="home-editor-bar">
                    <div>
                      <strong>
                        12 列工作桌 · {draft.layout.widgets.length} / 24 个部件
                      </strong>
                      <p>
                        {editor.mobile
                          ? "草稿已保留，回到桌面继续编辑。"
                          : "修改先留在草稿，保存后生成新版本。"}
                      </p>
                    </div>
                    <div className="home-actions">
                      <button
                        disabled={busy || editor.mobile}
                        onClick={() => setPanel("library")}
                      >
                        ＋ 部件库
                      </button>
                      <button
                        disabled={busy}
                        onClick={() => setPanel("history")}
                      >
                        版本历史
                      </button>
                      <button disabled={busy} onClick={cancel}>取消编辑</button>
                      <button
                        className="home-primary"
                        disabled={busy || conflict || editor.mobile}
                        onClick={() => void save()}
                      >
                        {busy ? "正在校验与保存…" : "保存为新版本"}
                      </button>
                    </div>
                  </div>
                  {conflict && (
                    <section className="home-conflict" role="alert">
                      <strong>主页已在别处更新，你的草稿已保留。</strong>
                      <p>
                        草稿基于 v{draft.baseVersion}，当前 v{state
                          .version}。先比较，再决定使用新布局或在新版本上继续。
                      </p>
                      <div className="home-actions">
                        <button onClick={() => setPanel("compare")}>
                          比较两份布局
                        </button>
                        <button
                          onClick={() => {
                            editor.begin(state.layout, state.version);
                            setForcedConflict(false);
                            setError("");
                            setNotice("已载入最新布局，原草稿已由你选择放弃。");
                          }}
                        >
                          放弃草稿，载入最新
                        </button>
                        <button
                          onClick={() => {
                            editor.setDraft({
                              ...draft,
                              baseVersion: state.version,
                              baseLayout: JSON.stringify(state.layout),
                            });
                            setForcedConflict(false);
                            setError("");
                            setNotice(
                              "保留你的草稿，在最新版本上继续；保存前仍会校验。",
                            );
                          }}
                        >
                          保留草稿，在最新版本继续
                        </button>
                      </div>
                    </section>
                  )}
                </>
              )}
              {currentLayout && (
                <HomeGrid
                  layout={currentLayout}
                  descriptors={descriptors}
                  sharedOnly={state?.source === "role"}
                  editing={!!draft && !editor.mobile}
                  onChange={editor.change}
                  onConfigure={setConfiguring}
                />
              )}
              {editor.mobile && draft && (
                <p className="home-mobile-note">
                  手机按布局顺序阅读，可以撤销、恢复历史；拖拽与配置请在桌面完成。
                </p>
              )}
            </>
          )}
        </>
      </DataState>
      {panel === "library" && draft && (
        <HomeLibrary
          descriptors={descriptors}
          count={draft.layout.widgets.length}
          onClose={() => setPanel(null)}
          onAdd={(descriptor) => {
            try {
              editor.change(addWidget(draft.layout, descriptor));
              setPanel(null);
              setNotice("已加入草稿，可以配置或调整后保存。");
            } catch (e) {
              setError(message(e));
              setPanel(null);
            }
          }}
        />
      )}
      {configuring && draft &&
        descriptors.find((item) => item.type === configuring.type) && (
        <ConfigPanel
          widget={configuring}
          descriptor={descriptors.find((item) =>
            item.type === configuring.type
          )!}
          onClose={() => setConfiguring(null)}
          onApply={(config) =>
            editor.change({
              ...draft.layout,
              widgets: draft.layout.widgets.map((widget) =>
                widget.id === configuring.id ? { ...widget, config } : widget
              ),
            })}
        />
      )}
      {panel === "history" && (
        <HomeHistoryPanel
          currentVersion={state?.version ?? 0}
          busy={busy}
          onClose={() => setPanel(null)}
          onRevert={(version) =>
            state &&
            void perform(
              () => revertHome(version, state.version),
              "已恢复历史布局，并生成新的当前版本。",
            )}
        />
      )}
      {panel === "ai" && (
        <HomePanel
          title="让你的 AI 布置工作桌"
          onClose={() => setPanel(null)}
        >
          <section>
            <h3>把这句话交给自己的 AI</h3>
            <p className="home-prompt">
              首页左边放我名下的逾期任务，右边放本季 OKR
              和灵感墙必读，去掉知识库搜索。
            </p>
            <p>
              在 Claude、Codex 或 WorkBuddy 里，通过中台 MCP
              改主页。网页会自动更新，每次修改留下版本，也能撤销。
            </p>
          </section>
          <section>
            <h3>第一次使用？</h3>
            <p>
              在对外接入中配置你的个人 Key，并开启 home:write
              范围。连接说明与部件清单由中台提供。
            </p>
            <Link to="/settings?section=open">打开设置 → 对外接入 ↗</Link>
          </section>
        </HomePanel>
      )}
      {panel === "compare" && draft && state && (
        <HomePanel
          title="比较草稿与当前布局"
          onClose={() => setPanel(null)}
        >
          {[["你的草稿 · 基于 v" + draft.baseVersion, draft.layout], [
            "当前已保存 · v" + state.version,
            state.layout,
          ]].map(([title, layout]) => (
            <section key={title as string}>
              <h3>{title as string}</h3>
              <ul>
                {sortedWidgets(layout as typeof draft.layout).map((widget) => (
                  <li key={widget.id}>
                    {widgetTitle(
                      widget,
                      descriptors.find((item) => item.type === widget.type),
                    )} · 第 {widget.x + 1} 列 / 第 {widget.y + 1} 行 ·{" "}
                    {widget.w}×{widget.h}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </HomePanel>
      )}
    </main>
  );
}
